import { createTheme } from '@mui/material/styles';
import { ptBR } from '@mui/material/locale';
import { GlobalStyles } from '@mui/material';
import { px2em } from './functions';
import {
  defaultPrimaryColor,
  defaultSecondaryColor,
  defaultThemeColor
} from './themes';

const muiTheme = createTheme(
  {
    palette: {
      primary: { main: '#1351B4' },
      secondary: { main: defaultSecondaryColor }
    },

    components: {
      // ====== OVERRIDES MIGRADOS ======

      MuiDialogTitle: {
        styleOverrides: {
          root: {
            background: defaultThemeColor,
            color: 'white'
          }
        }
      },

      MuiChip: {
        styleOverrides: {
          root: {
            fontSize: px2em(12)
          }
        }
      },

      MuiAutocomplete: {
        styleOverrides: {
          input: {
            minHeight: '20px'
          }
        }
      },

      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 0,
            display: 'flex',
            flexDirection: 'column',
            whiteSpace: 'pre-wrap'
          }
        }
      },

      // ── Campos desabilitados / somente leitura: manter contraste total ──────
      // GlobalStyles não sobrepõe os styleOverrides do MUI; a correção deve
      // estar aqui para ter a mesma especificidade e sobrescrever o
      // -webkit-text-fill-color: rgba(0,0,0,0.38) aplicado pelo MUI em campos disabled.
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&.Mui-disabled': {
              WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
              color: 'rgba(0, 0, 0, 0.87)',
            },
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              color: 'rgba(0, 0, 0, 0.6)',
              WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
            },
          },
          shrink: {
            // transform: 'translate(0, 1.5px) scale(.85)'
          }
        }
      },

      MuiSelect: {
        styleOverrides: {
          select: {
            '&.Mui-disabled': {
              WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
              color: 'rgba(0, 0, 0, 0.87)',
            },
          },
          icon: {
            '&.Mui-disabled': {
              fill: 'rgba(0, 0, 0, 0.54)',
            },
          },
        },
      },

      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            '&.Mui-disabled': {
              color: 'rgba(0, 0, 0, 0.87)',
              WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
            },
          },
        },
      },

      // ====== PROPS (agora: defaultProps) ======
      MuiTextField: {
        defaultProps: {
          // variant: 'filled', // se quiser, ative
        }
      }
    }
  },
  ptBR
);

// ====== CSS GLOBAL (substitui withStyles) ======
export const MuiGlobalCss = () => (
  <GlobalStyles
    styles={{
      '.MuiChip-root + .MuiChip-root.MuiChip-root': {
        marginLeft: '.5em'
      },

      // ── Campos desabilitados / somente leitura: manter contraste total ──────
      '.MuiInputBase-input.Mui-disabled, .MuiInputBase-root.Mui-disabled, .MuiFormControlLabel-label.Mui-disabled.Mui-disabled':
        {
          color: 'rgba(0, 0, 0, 0.87)',
          // webkit-text-fill-color sobrepõe "color" no Chrome/Safari; precisa ser
          // explicitado para cancelar o fade que o MUI aplica em campos disabled.
          WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
          cursor: 'not-allowed'
        },

      // Labels de campos disabled (InputLabel, FormLabel)
      '.MuiFormLabel-root.Mui-disabled, .MuiInputLabel-root.Mui-disabled': {
        color: 'rgba(0, 0, 0, 0.6)',        // mesmo tom do label normal (não-focado)
        WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
      },

      // Valor exibido no Select quando disabled
      '.MuiSelect-select.Mui-disabled': {
        WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
        color: 'rgba(0, 0, 0, 0.87)',
      },

      // Ícone de seta do Select quando disabled
      '.MuiSvgIcon-root.MuiSelect-icon.Mui-disabled': {
        fill: 'rgba(0, 0, 0, 0.54)',
      },

      // Autocomplete: texto do input quando disabled
      '.MuiAutocomplete-root .MuiInputBase-input.Mui-disabled': {
        WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
        color: 'rgba(0, 0, 0, 0.87)',
      },

      '.MuiRadio-colorPrimary.Mui-checked.Mui-disabled': {
        color: '#1351B4',
        cursor: 'not-allowed'
      },

      '.MuiFormGroup-root[role=radiogroup] .MuiFormControlLabel-root.Mui-disabled':
        {
          cursor: 'not-allowed'
        },

      '.MuiInputBase-input.MuiInput-input[readonly]': {
        cursor: 'not-allowed',
        color: 'rgba(0, 0, 0, 0.87)',
        WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
      },

      '.ksk-tagify[readonly]': {
        cursor: 'not-allowed',
        color: 'rgba(0, 0, 0, 0.87)'
      },

      '.MuiChip-root.Mui-disabled.Mui-disabled': {
        cursor: 'not-allowed',
        opacity: '1'
      },

      '.MuiInput-underline.Mui-disabled:before': {
        borderBottomStyle: 'solid'
      },

      '.MuiDialog-paper.MuiDialog-paper': {
        margin: '4px'
      },

      '.MuiAutocomplete-option[data-focus="true"]': {
        backgroundColor: '#D9E3F3'
      },

      '.MuiAutocomplete-inputRoot[class*="MuiInput-root"] .MuiAutocomplete-input.MuiAutocomplete-input:first-of-type':
        {
          padding: '4px 4px 5px'
        },

      '.MuiFormGroup-root.MuiFormGroup-row': {
        borderBottom: '1px solid #999',
        position: 'relative',
        top: '-2px',

        '& .MuiFormControlLabel-root': {
          marginBottom: '.3em'
        },

        '& .MuiTypography-root.MuiFormControlLabel-label': {
          fontSize: 'calc((13 / 14) * 1rem)'
        }
      },

      '.MuiRadio-root.MuiButtonBase-root.MuiIconButton-root': {
        padding: '0 6px 0 9px'
      },

      '.MuiSvgIcon-root.MuiSelect-icon': {
        height: '21px',
        width: '21px',
        fill: 'rgba(0, 0, 0, 0.54)',
        textAlign: 'start',
        position: 'absolute',
        right: '0px',
        top: '15.5px',
      }
    }}
  />
);

export default muiTheme;
